import React from 'react';
import { parseDate } from 'utils/datetime';
import { NavButton } from './NavButton';
import { useStartAndEndDate } from './hooks';
import { CalendarTile } from './CalendarTile';
import { SingleDateCalendarProps, NavDirection, Value } from './types';
import { StyledCalendar, StyledCoreCalendar } from './Calendar.styled';

export const SingleDateCalendar = React.forwardRef<HTMLDivElement, SingleDateCalendarProps>(
  (
    {
      blockFutureDates,
      blockPastDates,
      date,
      onDateChange,
      tileContent,
      validDateRange,
      styledAsCore,
      ...props
    },
    ref,
  ) => {
    const [minDate, maxDate] = useStartAndEndDate(validDateRange);
    const displayedDate = parseDate(date);

    const customTileContent =
      tileContent ||
      (({ date: tileDate }) => (
        <CalendarTile isSelected={tileDate.getTime() === displayedDate.getTime()} date={tileDate} />
      ));

    const onChange = (value: Value) => {
      /**
       * we need to do some type validation here as the value type that
       * the react-calendar onChange _can_ return is broader than what it
       * actually _does_ return (in our case).
       *
       * that's because there are props one can add to the Calendar component that change the output type,
       * such as `allowPartialProps` and `selectRange` that turn the calendar into a date range calendar,
       * and consequently update the onDateChange to return an array of values.
       *
       * Without those props enabled, the onChange returns a Date | Null.
       * so we're also type checking against that null value, because in the way we are using the calendar,
       * it never returns null.
       */
      if (!Array.isArray(value) && value !== null) {
        onDateChange(value);
      }
    };

    /**
     * the core/datepicker and core/dateRangePicker are still used in certain places, despite being deprecated.
     * they both now use the ui/calendar for their calendar, but retain the original core/ styling.
     * don't use this version.
     */
    const Calendar = styledAsCore ? StyledCoreCalendar : StyledCalendar;

    return (
      <div data-test="single-date-calendar" {...props} ref={ref}>
        <Calendar
          calendarType="gregory" // starts the week with sunday
          maxDate={blockFutureDates ? parseDate() : maxDate}
          maxDetail="month"
          minDate={blockPastDates ? parseDate() : minDate}
          minDetail="month"
          nextLabel={<NavButton styledAsCore={styledAsCore} direction={NavDirection.Next} />}
          onChange={onChange}
          prevLabel={<NavButton styledAsCore={styledAsCore} direction={NavDirection.Prev} />}
          showNeighboringMonth={false}
          tileContent={customTileContent}
          value={displayedDate}
        />
      </div>
    );
  },
);
